import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  sendOtp, verifyOtp, updateDriverProfile, getAuthErrorKey, auth,
  uploadDriverPhoto, uploadDriverVehiclePhoto,
  setNativeOtpCallbacks, hasPendingOtpSession,
  recordConsent, attestAgeAndConsent,
} from '../services/firebase';
import { useDriver, markAwaitingProfile, clearAwaitingProfile } from '../context/DriverContext';
import { useLanguage } from '../i18n/LanguageContext';
import { APP } from '../config/app';
import { PRIVACY_NOTICE_VERSION, ESSENTIAL_PURPOSES } from '../config/privacy';
import LangToggle from '../components/LangToggle';
import LoginOtpInput from '../components/LoginOtpInput';
import { listenForWebOtp } from '../utils/otpAutofill';
import { shouldUseNativePhoneAuth } from '../utils/platform';
import { capturePhoto } from '../utils/photoCapture';
import { openLegalUrl } from '../utils/legal';
import { isProfileComplete, validateKycPayload, isValidVehicleNumber } from '../utils/driverValidation';

const hasStoredPhoto = (url) => {
  const s = String(url || '');
  return s.startsWith('http') || s.startsWith('data:');
};

export default function Login({ startAt }) {
  const [step, setStep] = useState(startAt || 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [autoPhotoFile, setAutoPhotoFile] = useState(null);
  const [autoPhotoPreview, setAutoPhotoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // Two inputs per photo so the Anna gets an explicit choice instead of relying
  // on Android's file chooser: the `capture` one opens the camera straight away
  // (front for the selfie, rear for the auto), the plain one opens the gallery.
  const photoRef = useRef(null);
  const photoGalleryRef = useRef(null);
  const autoPhotoRef = useRef(null);
  const autoPhotoGalleryRef = useRef(null);
  const verifyingRef = useRef(false);
  const navigate = useNavigate();
  const { refreshDriverProfile, needsProfile, profileReady, profileLoaded, defaultsReady, user, driver } = useDriver();
  const { t } = useLanguage();

  useEffect(() => { if (startAt) setStep(startAt); }, [startAt]);
  useEffect(() => {
    if (user && profileReady && defaultsReady && profileLoaded) {
      if (needsProfile) {
        setStep('profile');
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, profileReady, defaultsReady, profileLoaded, needsProfile, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    if (driver && isProfileComplete(driver)) {
      navigate('/', { replace: true });
      return;
    }
    if (step !== 'profile' || !driver) return;
    if (!name && driver.name) setName(driver.name);
    if (!vehicleNumber && driver.vehicleNumber) setVehicleNumber(driver.vehicleNumber);
    if (!photoPreview && hasStoredPhoto(driver.photoUrl || driver.pendingPhotoUrl)) setPhotoPreview(driver.photoUrl || driver.pendingPhotoUrl);
    if (!autoPhotoPreview && hasStoredPhoto(driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl)) setAutoPhotoPreview(driver.vehiclePhotoUrl || driver.pendingVehiclePhotoUrl || driver.rcPhotoUrl);
  }, [step, driver, navigate]);

  const pickPhoto = (kind, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (kind === 'face') {
      if (photoPreview && photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
      setPhotoFile(file);
      setPhotoPreview(url);
    } else {
      if (autoPhotoPreview && autoPhotoPreview.startsWith('blob:')) URL.revokeObjectURL(autoPhotoPreview);
      setAutoPhotoFile(file);
      setAutoPhotoPreview(url);
    }
  };

  // Camera / gallery button → native picker first (the hidden <input capture>
  // does not reliably open the camera in the Android WebView), <input> fallback
  // on web or if the plugin call fails.
  const takePhoto = async (kind, { source, direction, inputRef }) => {
    try {
      const blob = await capturePhoto({ source, direction });
      if (blob) { pickPhoto(kind, blob); return; }
    } catch (e) {
      console.warn('camera pick failed:', e?.message || e);
      toast.error(t('login.photoPickError') || 'Could not open the camera — pick from gallery');
    }
    inputRef.current?.click();
  };

  const finishAfterAuth = useCallback(async (firebaseUser) => {
    // Read the authoritative profile BEFORE writing anything to the driver doc.
    // attestAgeAndConsent does a merge-write; if it runs first, that pending
    // write shadows the real doc with a name-less/vehicle-less overlay and a
    // returning driver gets misrouted into the registration form.
    const profile = await refreshDriverProfile(firebaseUser.uid);
    const isReturningDriver = isProfileComplete(profile);
    if (isReturningDriver) {
      clearAwaitingProfile();
      navigate('/', { replace: true });
    } else {
      markAwaitingProfile(firebaseUser.uid);
      setStep('profile');
    }

    // Consent / age attestation — after routing, best-effort, never blocking.
    try {
      await attestAgeAndConsent(firebaseUser.uid, PRIVACY_NOTICE_VERSION);
      await recordConsent(firebaseUser.uid, {
        purposes: [...ESSENTIAL_PURPOSES],
        noticeVersion: PRIVACY_NOTICE_VERSION,
        method: 'login_otp',
      });
    } catch (e) {
      console.warn('privacy consent record failed', e?.message);
    }
  }, [refreshDriverProfile, navigate]);

  const handleSendOtp = async () => {
    if (phone.length < 10) { toast.error(t('login.invalidPhone')); return; }
    setLoading(true);
    try {
      const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (recaptchaKey && !document.querySelector('script[src*="recaptcha/enterprise"]')) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaKey}`;
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const sent = await sendOtp(`+91${phone}`);
      if (sent?.autoVerified) {
        const u = auth.currentUser;
        if (u) {
          await finishAfterAuth(u);
          return;
        }
      }
      setOtp('');
      setResendIn(30);
      toast.success(t('login.otpSent'));
      setStep('otp');
    } catch (e) {
      toast.error(t(getAuthErrorKey(e)));
    } finally { setLoading(false); }
  };

  const completeOtpLogin = useCallback(async (code) => {
    const digits = String(code || '').replace(/\D/g, '').slice(0, 6);
    if (!digits || digits.length < 6) { toast.error(t('login.invalidOtp')); return; }
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setLoading(true);
    try {
      if (auth.currentUser) {
        await finishAfterAuth(auth.currentUser);
        return;
      }
      const firebaseUser = await verifyOtp(digits);
      if (firebaseUser) {
        await finishAfterAuth(firebaseUser);
      }
    } catch (e) {
      if (auth.currentUser) {
        await finishAfterAuth(auth.currentUser);
        return;
      }
      const key = getAuthErrorKey(e);
      if (!auth.currentUser) {
        toast.error(t(key));
      }
      if (key === 'authErrors.codeExpired') {
        setOtp('');
        setResendIn(0);
        toast(t('login.otpExpiredHint'), { icon: 'ℹ️', duration: 4000 });
      }
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  }, [t, finishAfterAuth]);

  useEffect(() => {
    if (!shouldUseNativePhoneAuth()) return undefined;
    setNativeOtpCallbacks({
      onAutoVerified: async () => {
        const u = auth.currentUser;
        if (!u) return;
        await finishAfterAuth(u);
      },
      onCodeReceived: (code) => {
        if (auth.currentUser) return;
        const digits = String(code || '').replace(/\D/g, '').slice(0, 6);
        if (digits.length < 6 || !hasPendingOtpSession()) return;
        setOtp(digits);
        completeOtpLogin(digits);
      },
    });
    return () => setNativeOtpCallbacks({ onAutoVerified: null, onCodeReceived: null });
  }, [completeOtpLogin, finishAfterAuth]);

  useEffect(() => {
    if (step !== 'otp') return undefined;
    const ac = new AbortController();
    listenForWebOtp((code) => {
      const digits = code.replace(/\D/g, '').slice(0, 6);
      setOtp(digits);
      completeOtpLogin(digits);
    }, ac.signal);
    return () => ac.abort();
  }, [step, completeOtpLogin]);

  const handleSaveProfile = async () => {
    const activeUid = user?.uid || auth.currentUser?.uid;
    if (!activeUid) {
      toast.error('Session expired. Please log in again.');
      return;
    }
    const cleanName = name.trim();
    if (!cleanName || !vehicleNumber.trim()) return;
    if (/^\d{4,8}$/.test(cleanName)) {
      toast.error('Please enter your driver name (e.g. Ramesh Anna), not the OTP code.');
      return;
    }
    if (cleanName.length < 2) {
      toast.error('Please enter a valid name.');
      return;
    }
    if (!isValidVehicleNumber(vehicleNumber)) {
      toast.error(t('login.invalidVehicle') || 'Enter a valid auto number (for example, AP39TG4838).');
      return;
    }
    const haveFace = photoFile || hasStoredPhoto(photoPreview) || hasStoredPhoto(driver?.photoUrl || driver?.pendingPhotoUrl);
    const haveAuto = autoPhotoFile || hasStoredPhoto(autoPhotoPreview) || hasStoredPhoto(driver?.vehiclePhotoUrl || driver?.pendingVehiclePhotoUrl);
    if (!haveFace || !haveAuto) {
      toast.error(t('login.photoRequired'));
      return;
    }
    setLoading(true);
    try {
      let faceUrl = driver?.photoUrl || driver?.pendingPhotoUrl || null;
      let autoUrl = driver?.vehiclePhotoUrl || driver?.pendingVehiclePhotoUrl || null;

      // Upload both photos in parallel for 2x faster response
      const uploads = [];
      if (photoFile) {
        uploads.push(
          uploadDriverPhoto(activeUid, photoFile)
            .then((url) => { faceUrl = url; })
            .catch((err) => console.warn('Face photo upload failed:', err)),
        );
      }
      if (autoPhotoFile) {
        uploads.push(
          uploadDriverVehiclePhoto(activeUid, autoPhotoFile)
            .then((url) => { autoUrl = url; })
            .catch((err) => console.warn('Auto photo upload failed:', err)),
        );
      }

      if (uploads.length > 0) {
        await Promise.allSettled(uploads);
      }

      if ((photoFile && !faceUrl) || (autoPhotoFile && !autoUrl)) {
        toast.error('Photo upload failed. Please check network and retry.');
        setLoading(false);
        return;
      }

      const rawPhone = user?.phoneNumber || auth.currentUser?.phoneNumber || (phone ? `+91${phone}` : '') || driver?.phone;
      const clean10 = String(rawPhone || '').replace(/\D/g, '').slice(-10);
      const normPhone = clean10.length === 10 ? `+91${clean10}` : (rawPhone || '');

      const kyc = validateKycPayload({
        name: cleanName,
        vehicleNumber,
        photoUrl: faceUrl,
        vehiclePhotoUrl: autoUrl,
      });
      if (!kyc.valid) {
        toast.error(kyc.error);
        return;
      }

      await updateDriverProfile(activeUid, {
        ...kyc.normalized,
        ...(normPhone ? { phone: normPhone } : {}),
        // Explicitly include safe defaults so this write satisfies the Firestore
        // 'create' rule if the skeleton doc was never persisted (e.g. network hiccup
        // during first launch). setDoc(merge:true) on a missing doc = create op.
        isApproved: false,
        isBlocked: false,
      });

      clearAwaitingProfile();
      await refreshDriverProfile(activeUid, { force: true }).catch(() => {});
      toast.success('Profile submitted for verification!');
      navigate('/', { replace: true });
    } catch (e) {
      console.error('Save driver profile failed:', e);
      toast.error('Could not save profile. Please check your network.');
    } finally { setLoading(false); }
  };

  const canSave = Boolean(
    name.trim()
    && !/^\d{4,8}$/.test(name.trim())
    && isValidVehicleNumber(vehicleNumber)
    && (photoFile || hasStoredPhoto(photoPreview) || hasStoredPhoto(driver?.photoUrl))
    && (autoPhotoFile || hasStoredPhoto(autoPhotoPreview) || hasStoredPhoto(driver?.vehiclePhotoUrl)),
  );

  if (user && !profileLoaded) {
    return (
      <div className="app login-screen" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', textAlign: 'center' }}>
        <img
          src="/icons/icon-192.png"
          alt="MyTownly Anna"
          style={{
            width: 84,
            height: 84,
            borderRadius: 22,
            marginBottom: 20,
            boxShadow: '0 8px 28px rgba(0, 168, 107, 0.35)',
            objectFit: 'cover',
          }}
        />
        <div className="pulse-dot" style={{ width: 14, height: 14, background: '#00A86B', borderRadius: '50%', marginBottom: 14 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{t('login.signingIn')}</h2>
        <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>{t('login.loadingProfile')}</p>
      </div>
    );
  }

  return (
    <div className="app login-screen">
      <div className="login-top"><LangToggle /></div>
      <div className="login-hero">
        <img
          src="/icons/icon-192.png"
          alt="MyTownly Anna"
          style={{
            width: 84,
            height: 84,
            borderRadius: 22,
            marginBottom: 14,
            boxShadow: '0 8px 28px rgba(0, 168, 107, 0.35)',
            objectFit: 'cover',
          }}
        />
        <h1>{t('login.title')}</h1>
        <p className="text-muted">{t('login.subtitle', { city: APP.city })}</p>
      </div>

      {step === 'phone' && (
        <>
          <label className="text-muted field-label">{t('login.phoneLabel')}</label>
          <div className="phone-row">
            <span className="phone-prefix">+91</span>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder={t('login.phonePlaceholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              autoComplete="tel-national"
            />
          </div>
          <button type="button" className="btn btn-green" disabled={loading || phone.length < 10} onClick={handleSendOtp}>
            {t('login.sendOtp')}
          </button>
          <p className="login-legal-consent text-muted text-center" style={{ fontSize: '11px', marginTop: '16px', lineHeight: '1.4', color: '#64748b' }}>
            {t('login.legalConsentPrefix')}
            <button
              type="button"
              onClick={() => navigate('/legal/terms')}
              style={{ background: 'none', border: 'none', padding: 0, color: '#00A86B', textDecoration: 'underline', font: 'inherit', fontSize: 'inherit', cursor: 'pointer', fontWeight: 600 }}
            >
              {t('login.termsOfService')}
            </button>
            {t('login.and')}
            <button
              type="button"
              onClick={() => navigate('/legal/privacy')}
              style={{ background: 'none', border: 'none', padding: 0, color: '#00A86B', textDecoration: 'underline', font: 'inherit', fontSize: 'inherit', cursor: 'pointer', fontWeight: 600 }}
            >
              {t('login.privacyPolicy')}
            </button>
            {t('login.legalConsentSuffix') || ''}
          </p>
        </>
      )}

      {step === 'otp' && (
        <div className="login-otp-panel">
          <p className="login-otp-phone">+91 {phone}</p>
          <p className="text-muted text-center">{t('login.otpSent')}</p>
          <p className="login-otp-hint text-muted text-center">{t('login.otpAutofillHint')}</p>
          <LoginOtpInput
            value={otp}
            onChange={setOtp}
            onComplete={completeOtpLogin}
            disabled={loading}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-green"
            disabled={loading || String(otp).replace(/\D/g, '').length < 6}
            onClick={() => completeOtpLogin(otp)}
          >
            {loading ? t('login.verifying') : t('login.verifyOtp')}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={loading || resendIn > 0}
            onClick={handleSendOtp}
          >
            {resendIn > 0 ? t('login.resendWait', { s: resendIn }) : t('login.resend')}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={loading}
            onClick={() => { setStep('phone'); setOtp(''); }}
          >
            {t('login.changeNumber')}
          </button>
        </div>
      )}

      {step === 'profile' && (
        <>
          <label className="text-muted field-label">{t('login.nameLabel')}</label>
          <input
            type="text"
            placeholder={t('login.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <label className="text-muted field-label">{t('login.vehicleLabel')}</label>
          <input
            type="text"
            placeholder={t('login.vehiclePlaceholder')}
            value={vehicleNumber}
            onChange={(e) => setVehicleNumber(e.target.value)}
          />

          <label className="text-muted field-label">{t('login.autoPhotoLabel')}</label>
          <p className="login-photo-hint">{t('login.autoPhotoHint')}</p>
          <button
            type="button"
            className="login-photo-pick"
            onClick={() => takePhoto('auto', { source: 'camera', direction: 'rear', inputRef: autoPhotoRef })}
            disabled={loading}
          >
            {autoPhotoPreview ? (
              <img src={autoPhotoPreview} alt="" className="login-photo-preview" />
            ) : (
              <span className="login-photo-placeholder">🛺</span>
            )}
            <span className="login-photo-cta">
              {autoPhotoPreview ? t('login.changePhoto') : t('login.addPhoto')}
            </span>
          </button>
          <div className="login-photo-actions">
            <button
              type="button"
              className="login-photo-action"
              onClick={() => takePhoto('auto', { source: 'camera', direction: 'rear', inputRef: autoPhotoRef })}
              disabled={loading}
            >
              📷 {t('login.takePhoto')}
            </button>
            <button
              type="button"
              className="login-photo-action"
              onClick={() => takePhoto('auto', { source: 'photos', inputRef: autoPhotoGalleryRef })}
              disabled={loading}
            >
              🖼️ {t('login.chooseGallery')}
            </button>
          </div>
          <input
            ref={autoPhotoRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => { pickPhoto('auto', e.target.files?.[0]); e.target.value = ''; }}
          />
          <input
            ref={autoPhotoGalleryRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { pickPhoto('auto', e.target.files?.[0]); e.target.value = ''; }}
          />

          <label className="text-muted field-label">{t('login.photoLabel')}</label>
          <p className="login-photo-hint">{t('login.photoHint')}</p>
          <button
            type="button"
            className="login-photo-pick"
            onClick={() => takePhoto('face', { source: 'camera', direction: 'front', inputRef: photoRef })}
            disabled={loading}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="" className="login-photo-preview" />
            ) : (
              <span className="login-photo-placeholder">📷</span>
            )}
            <span className="login-photo-cta">
              {photoPreview ? t('login.changePhoto') : t('login.addPhoto')}
            </span>
          </button>
          <div className="login-photo-actions">
            <button
              type="button"
              className="login-photo-action"
              onClick={() => takePhoto('face', { source: 'camera', direction: 'front', inputRef: photoRef })}
              disabled={loading}
            >
              📷 {t('login.takePhoto')}
            </button>
            <button
              type="button"
              className="login-photo-action"
              onClick={() => takePhoto('face', { source: 'photos', inputRef: photoGalleryRef })}
              disabled={loading}
            >
              🖼️ {t('login.chooseGallery')}
            </button>
          </div>
          <input
            ref={photoRef}
            type="file"
            accept="image/*"
            capture="user"
            hidden
            onChange={(e) => { pickPhoto('face', e.target.files?.[0]); e.target.value = ''; }}
          />
          <input
            ref={photoGalleryRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { pickPhoto('face', e.target.files?.[0]); e.target.value = ''; }}
          />

          <button
            type="button"
            className="btn btn-green"
            disabled={loading || !canSave}
            onClick={handleSaveProfile}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {loading ? (
              <>
                <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                <span>Submitting for approval…</span>
              </>
            ) : (
              t('login.saveProfile')
            )}
          </button>
        </>
      )}
    </div>
  );
}
